import { createContext, useContext, useRef, useState } from "react";
import PopupContainer from "../components/PopupContainer";

interface PopupContextType {
  openPopup: <P, T = any>(
    component: React.ComponentType<P>,
    props?: P,
    onClose?: () => void,
    useUKIStyling?: boolean
  ) => Promise<T | undefined>;

  closePopup: (result?: any) => void;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const usePopup = (): PopupContextType => {
    const context = useContext(PopupContext);
    if (!context) {
        throw new Error("usePopup must be used within PopupContextProvider");
    }
    return context;
};

export const PopupContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [Component, setComponent] = useState<React.ComponentType<any> | null>(null);
    const [popupProps, setPopupProps] = useState<any>({});
    const [useUKIStyling, setUseUKIStyling] = useState<boolean>(false);
    const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | null>(null);

    const resolverRef = useRef<((value: any) => void) | null>(null);

    const openPopup = <T = any>(
        component: React.ComponentType<any>,
        props: any = {},
        onClose?: () => void,
        useUKIStyling: boolean = false
    ): Promise<T | undefined> => {
        setComponent(() => component);
        setPopupProps(props);
        setUseUKIStyling(useUKIStyling);
        setOnCloseCallback(() => onClose ?? null);
        setIsOpen(true);

        return new Promise<T | undefined>((resolve) => {
            resolverRef.current = resolve;
        });
    };
   
    const closePopup: PopupContextType["closePopup"] = (result?: any) => {
        setIsOpen(false);
        setComponent(null);
        setPopupProps({});

        resolverRef.current?.(result);
        resolverRef.current = null;

        if (onCloseCallback) {
            onCloseCallback();
            setOnCloseCallback(null);
        }
    };

    return (
        <PopupContext.Provider value={{ openPopup, closePopup }}>
            {children}
            <PopupContainer
                isOpen={isOpen}
                useUKIStyling={useUKIStyling}
                onClose={() => closePopup()}
                Component={Component}
                props={popupProps}
            />
        </PopupContext.Provider>
    );
};

